export type BatchLoaderOptions = {
  /** The most keys handed to one `batchFn` call; a larger window is split into several calls. */
  maxBatchSize: number;
  /** How long to collect keys, from the first one, before the batch is sent. */
  windowMs: number;
};

/**
 * Resolves keys in one call. A key mapped to an `Error` rejects only that key, so one failed item does not
 * fail the others; throwing rejects every key of the call.
 */
export type BatchFn<TValue> = (keys: string[]) => Promise<Map<string, TValue | Error>>;

export type BatchLoader<TValue> = {
  load: (key: string) => Promise<TValue>;
};

type Waiter<TValue> = { resolve: (value: TValue) => void; reject: (error: unknown) => void };

/**
 * Collects the keys asked for within a short window and resolves them with one `batchFn` call per
 * `maxBatchSize` keys. Each caller still gets a promise for its own key, so a table cell can keep asking for
 * "its" row while the requests go out together.
 */
export function createBatchLoader<TValue>(
  batchFn: BatchFn<TValue>,
  { maxBatchSize, windowMs }: BatchLoaderOptions
): BatchLoader<TValue> {
  if (maxBatchSize < 1) throw new RangeError('maxBatchSize must be at least 1');

  let pending = new Map<string, Waiter<TValue>[]>();
  let timer: ReturnType<typeof setTimeout> | undefined;

  const run = async (keys: string[], waiters: Map<string, Waiter<TValue>[]>) => {
    try {
      const results = await batchFn(keys);
      for (const key of keys) {
        const result = results.get(key);
        for (const waiter of waiters.get(key) ?? []) {
          if (result === undefined) waiter.reject(new Error(`No result for ${key}`));
          else if (result instanceof Error) waiter.reject(result);
          else waiter.resolve(result);
        }
      }
    } catch (error) {
      for (const key of keys) waiters.get(key)?.forEach((waiter) => waiter.reject(error));
    }
  };

  const flush = () => {
    timer = undefined;
    const waiters = pending;
    pending = new Map();
    const keys = [...waiters.keys()];
    for (let start = 0; start < keys.length; start += maxBatchSize) {
      void run(keys.slice(start, start + maxBatchSize), waiters);
    }
  };

  return {
    load: (key) =>
      new Promise<TValue>((resolve, reject) => {
        const waiters = pending.get(key);
        if (waiters) waiters.push({ resolve, reject });
        else pending.set(key, [{ resolve, reject }]);
        timer ??= setTimeout(flush, windowMs);
      }),
  };
}
